import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Zap, Server, Layers, Check } from 'lucide-react';

/**
 * CapacityControl — the single place to choose spot vs on-demand (CAST-parity).
 *
 * In the engine-as-SoR model, capacity type is a POLICY the in-cluster engine
 * honors when it picks node shapes — it maps to the NodePool's
 * karpenter.sh/capacity-type requirement. This replaces the legacy backend
 * "Spot Policy" toggle (which drove the retired recommender). One control, one
 * source of truth, read by the engine.
 *
 * Reads the effective capacity from the node-optimization config; writes it back
 * via the existing rebalance-config endpoint (node_optimization_config.capacity_type),
 * which the agent reconciles onto the pool. Optimistic + reverts on error.
 */
const OPTIONS = [
  { key: 'on-demand', label: 'On-Demand', Icon: Server, hint: 'Stable price, no interruptions — disables spot savings, fallback and revert-to-spot' },
  { key: 'mixed', label: 'Mixed', Icon: Layers, hint: 'Spot where safe, on-demand fallback' },
  { key: 'spot', label: 'Spot', Icon: Zap, hint: 'Lowest cost, interruptible' },
];

const CapacityControl = ({ clusterId }) => {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(null);
  const [savedAt, setSavedAt] = useState(false);

  const load = useCallback(async () => {
    if (!clusterId) return;
    try {
      const r = await axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-config`);
      const cfg = r.data || {};
      setValue(cfg.capacity_type || cfg.spot_policy || 'on-demand');
    } catch {
      setValue('on-demand');
    }
  }, [clusterId]);

  useEffect(() => { load(); }, [load]);

  const choose = async (key) => {
    if (key === value || saving) return;
    const prev = value;
    setValue(key); setSaving(key); setSavedAt(false);
    try {
      await axios.post(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-config`, {
        capacity_type: key,
        spot_policy: key, // keep the legacy field coherent during transition
      });
      setSavedAt(true);
      setTimeout(() => setSavedAt(false), 2000);
    } catch {
      setValue(prev); // revert on failure
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between px-5 pt-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100" title="Applies to this cluster's node pool (karpenter.sh/capacity-type)">Capacity</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Choose how new nodes are purchased.
          </p>
        </div>
        {saving ? (
          <span className="text-xs font-medium text-slate-400 dark:text-slate-500">Saving…</span>
        ) : savedAt ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <Check className="h-3.5 w-3.5" /> Saved
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-2 p-4">
        {OPTIONS.map(({ key, label, Icon, hint }) => {
          const active = value === key;
          return (
            <button
              key={key}
              onClick={() => choose(key)}
              disabled={!!saving}
              className={[
                'group relative flex flex-col items-start gap-2 rounded-lg border px-3.5 py-3 text-left transition-all',
                active
                  ? 'border-emerald-600 dark:border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40'
                  : 'border-slate-200 dark:border-slate-800 bg-transparent hover:border-slate-300 dark:hover:border-slate-700',
              ].join(' ')}
            >
              <Icon className={`h-4 w-4 ${active ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-400'}`} />
              <div>
                <div className={`text-sm font-medium tracking-tight ${active ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-200'}`}>
                  {label}
                </div>
                <div className={`text-[11px] leading-tight mt-0.5 ${active ? 'text-emerald-700/70 dark:text-emerald-300/70' : 'text-slate-400'}`}>
                  {hint}
                </div>
              </div>
              {active && (
                <Check className="absolute right-2 top-2 h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CapacityControl;
